import { useState, useEffect, createContext, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { logoutpath } from "../store/actions/AuthActions";
import { getAdminCredentials, logoutAdmin } from "../api/AdminApi";
import Swal from "sweetalert2";
import AdminActivityTracker from "../services/AdminActivityTracker";

const AuthContext = createContext({
  token: null,
  userData: null,
  isLogged: false,
  login: () => {},
  logout: () => {},
});

let inactivityTimer;
let logoutTimer;

export function getRemainingTime(expiration) {
  const expDate = new Date(expiration).getTime();
  return expDate - new Date().getTime();
}

export const AuthContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [token, setToken] = useState(null);
  const [userData, setUserData] = useState(null);
  const isLogged = !!token;

  const clearTimers = () => {
    clearTimeout(inactivityTimer);
    clearTimeout(logoutTimer);
  };

  // logout ahora recibe history siempre
 const logout = useCallback(() => {
  logoutAdmin().catch(() => {});
  clearTimers();
  setToken(null);
  setUserData(null);
  localStorage.removeItem("token");
  localStorage.removeItem("expiration");
  localStorage.removeItem("userData");
  sessionStorage.removeItem("token");
  sessionStorage.removeItem("expiration");
  sessionStorage.removeItem("userData");
  dispatch(logoutpath(history)); // usa history capturado
}, [dispatch, history]);

const startInactivityWatch = useCallback(() => {
  clearTimeout(inactivityTimer);
  inactivityTimer = setTimeout(() => {
    logout(); // ✅ sin history
    Swal.fire({
      icon: "info",
      title: "Sesión finalizada",
      text: "Has sido desconectado por inactividad.",
      confirmButtonText: "Aceptar",
      confirmButtonColor: "#3085d6",
    });
  }, 15 * 60 * 1000);
}, [logout]);
const login = useCallback(
  async (newToken, expirationDate, remember, newUserData) => {
    setToken(newToken);
    setUserData(newUserData);

    const storage = remember ? localStorage : sessionStorage;
    storage.setItem("token", newToken);
    storage.setItem("expiration", expirationDate);
    storage.setItem("userData", JSON.stringify(newUserData));

    const userdata = await getAdminCredentials();
    console.log(userdata);

    const timeLeft = getRemainingTime(expirationDate);
    clearTimeout(logoutTimer);
    logoutTimer = setTimeout(() => logout(), timeLeft);

    // 👉 Solo activar inactividad si NO es tipo 0 o 2
    if (userdata.IdTipoUsuario !== "0" && userdata.IdTipoUsuario !== "2") {
      ["click", "mousemove", "keypress", "scroll"].forEach((evt) =>
        window.addEventListener(evt, startInactivityWatch)
      );
      startInactivityWatch();
    }
  },
  [logout, startInactivityWatch]
);



  // Auto‑login al montar, y si expira o está vencido, pasamos history
  useEffect(() => {
    const storages = [localStorage, sessionStorage];
    for (const s of storages) {
      const storedToken = s.getItem("token");
      const storedExp = s.getItem("expiration");
      const storedUser = s.getItem("userData");

      if (storedToken && storedExp) {
        const timeLeft = getRemainingTime(storedExp);
        if (timeLeft > 0) {
          let parsedUser = null;
          if (storedUser && storedUser !== "undefined") {
            try {
              parsedUser = JSON.parse(storedUser);
            } catch {
              parsedUser = null;
            }
          }
          login(storedToken, storedExp, s === localStorage, parsedUser);
        } else {
          logout(history);
        }
        break;
      }
    }
  }, [login, logout, history]);

  useEffect(() => {
    if (!token) return undefined;
    const validateCurrentSession = async () => {
      try {
        await getAdminCredentials();
      } catch (error) {
        if (/sesión|revocada|authenticated|jwt|token/i.test(error?.message || "")) {
          logout();
          Swal.fire({ icon: "info", title: "Sesión cerrada", text: "Un administrador cerró esta sesión o el acceso expiró." });
        }
      }
    };
    const interval = window.setInterval(validateCurrentSession, 60000);
    return () => window.clearInterval(interval);
  }, [token, logout]);

  // Cleanup de listeners al desmontar
useEffect(() => {
  return () => {
    clearTimers();
    ["click", "mousemove", "keypress", "scroll"].forEach((evt) =>
      window.removeEventListener(evt, startInactivityWatch)
    );
  };
}, [startInactivityWatch]);


  return (
    <AuthContext.Provider value={{ token, userData, isLogged, login, logout }}>
      <AdminActivityTracker enabled={isLogged} />
      {children}
    </AuthContext.Provider>
  );
};

export function getAuthToken() {
  let token = localStorage.getItem("token");
  let expiration = localStorage.getItem("expiration");
  let storage = localStorage;

  if (!token) {
    token = sessionStorage.getItem("token");
    expiration = sessionStorage.getItem("expiration");
    storage = sessionStorage;
  }

  if (!token) {
    console.debug("[Auth] No token found in any storage");
    return null;
  }

  if (expiration && new Date() > new Date(expiration)) {
    console.debug("[Auth] Token expired at", expiration);
    storage.removeItem("token");
    storage.removeItem("expiration");
    return null;
  }

  return token;
}

export default AuthContext;
